<aside
    class="booking-summary-card"
    aria-label="Booking summary"
    data-testid="booking-summary-card"
>
    @if (summary().venueImageUrl; as venueImageUrl) {
        <img
            class="booking-summary-venue-image"
            [src]="venueImageUrl"
            width="1200"
            height="675"
            [alt]="summary().venueName + ' image'"
            data-testid="booking-summary-venue-image"
        />
    }

    <div class="booking-summary-segment booking-summary-overview">
        <h2
            class="h6 fw-semibold m-0"
            data-testid="booking-summary-venue-name"
        >
            {{ summary().venueName }}
        </h2>
    </div>

    <section
        class="booking-summary-segment booking-summary-details"
        aria-label="Booking details"
    >
        @if (summary().choice; as choice) {
            <strong
                class="booking-summary-choice"
                data-testid="booking-summary-choice"
            >
                {{ choice.displayName }}
            </strong>
        }

        <div class="booking-summary-detail" [class.booking-summary-detail--change]="changes().date">
            <img
                src="assets/calendar_small.svg"
                width="20"
                height="20"
                alt=""
                aria-hidden="true"
            />
            @if (changes().date && summary().previous; as previous) {
                <s class="text-secondary">
                    <span class="visually-hidden">Previous date: </span>
                    <time [attr.datetime]="previous.dateTime.toISOString()">{{ previous.dateTime | date:'EEEE d MMMM y' }}</time>
                </s>
                <img src="assets/right/line.svg" width="20" height="20" alt="" aria-hidden="true" />
            }
            <time class="booking-summary-new" [attr.datetime]="summary().dateTime.toISOString()">
                <span class="visually-hidden">{{ changes().date ? 'New date: ' : 'Date: ' }}</span>
                {{ summary().dateTime | date:'EEEE d MMMM y' }}
            </time>
        </div>

        <div class="booking-summary-detail booking-summary-time" [class.booking-summary-detail--change]="changes().time">
            <img
                src="assets/clock_small.svg"
                width="20"
                height="20"
                alt=""
                aria-hidden="true"
            />
            @if (changes().time && summary().previous; as previous) {
                <s class="text-secondary">
                    <span class="visually-hidden">Previous time: </span>
                    <time [attr.datetime]="previous.dateTime.toISOString()">{{ previous.dateTime | date:'shortTime' }}</time>
                    &ndash;
                    <time [attr.datetime]="previous.endDateTime.toISOString()">
                        @if (previous.dateTime.toDateString() !== previous.endDateTime.toDateString()) {
                            {{ previous.endDateTime | date:'EEE d MMM, HH:mm' }}
                        } @else {
                            {{ previous.endDateTime | date:'shortTime' }}
                        }
                    </time>
                </s>
                <img src="assets/right/line.svg" width="20" height="20" alt="" aria-hidden="true" />
            }
            <span class="booking-summary-new">
                <span class="visually-hidden">{{ changes().time ? 'New time: ' : 'Time: ' }}</span>
                <time [attr.datetime]="summary().dateTime.toISOString()">
                    {{ summary().dateTime | date:'shortTime' }}
                </time>
                &ndash;
                <time [attr.datetime]="summary().endDateTime.toISOString()">
                    @if (endsOnDifferentDay()) {
                        {{ summary().endDateTime | date:'EEE d MMM, HH:mm' }}
                    } @else {
                        {{ summary().endDateTime | date:'shortTime' }}
                    }
                </time>
            </span>
        </div>

        <div class="booking-summary-detail" [class.booking-summary-detail--change]="changes().partySize">
            <img
                src="assets/people/normal.svg"
                width="20"
                height="20"
                alt=""
                aria-hidden="true"
            />
            @if (changes().partySize && summary().previous; as previous) {
                <s class="text-secondary">
                    <span class="visually-hidden">Previous party size: </span>
                    {{ previous.partySize | i18nPlural: partySizePluralMapping }}
                </s>
                <img src="assets/right/line.svg" width="20" height="20" alt="" aria-hidden="true" />
            }
            <span class="booking-summary-new">
                <span class="visually-hidden">{{ changes().partySize ? 'New party size: ' : 'Party size: ' }}</span>
                {{ summary().partySize | i18nPlural: partySizePluralMapping }}
            </span>
        </div>

        @if (summary().areaName; as areaName) {
            <div class="booking-summary-detail" [class.booking-summary-detail--change]="changes().area">
                <img
                    src="assets/location.svg"
                    width="20"
                    height="20"
                    alt=""
                    aria-hidden="true"
                />
                @if (changes().area && summary().previous; as previous) {
                    <s class="text-secondary">
                        <span class="visually-hidden">Previous area: </span>
                        {{ previous.areaName ?? 'Not specified' }}
                    </s>
                    <img src="assets/right/line.svg" width="20" height="20" alt="" aria-hidden="true" />
                }
                <span class="booking-summary-new">
                    <span class="visually-hidden">{{ changes().area ? 'New area: ' : 'Area: ' }}</span>
                    {{ areaName }}
                </span>
            </div>
        }
    </section>

    @if (summary().items; as items) {
        <section class="booking-summary-segment" aria-label="Bookings in your basket" data-testid="basket-summary-items">
            @for (item of items; track item.id) {
                <div class="vstack gap-1 mb-3">
                    <strong>{{ item.reasonName }}</strong>
                    <span>{{ item.start | date:'shortTime' }} – {{ item.end | date:'shortTime' }}</span>
                    @if (item.depositAmount > 0) {
                        <span>{{ item.paymentLabel ?? 'Deposit' }}: {{ null | currencyUnit:item.depositAmount:summary().currencyCode }}</span>
                    }
                    @for (extra of item.extras; track $index) {
                        <div class="d-flex justify-content-between gap-3">
                            <span>
                                {{ extra.quantity }} &times; {{ extra.name }}
                                @if (extra.modifierNames?.length) {
                                    <small class="d-block text-secondary">{{ extra.modifierNames?.join(', ') }}</small>
                                }
                                @if (extra.unitAmount !== undefined) {
                                    <small class="d-block text-secondary">{{ null | currencyUnit:extra.unitAmount:summary().currencyCode }} each</small>
                                    <app-booking-product-refund-policy [policy]="extra" />
                                }
                            </span>
                            @if (extra.unitAmount !== undefined) {
                                <span class="text-nowrap">{{ null | currencyUnit:extra.unitAmount * extra.quantity:summary().currencyCode }}</span>
                            }
                        </div>
                    }
                </div>
            }
        </section>
    }

    @if (summary().extras.length > 0) {
        <section
            class="booking-summary-segment booking-summary-section"
            aria-labelledby="booking-summary-extras-title"
            data-testid="booking-summary-extras"
        >
            <h3
                id="booking-summary-extras-title"
                class="h6 fw-semibold m-0"
            >
                Extras
            </h3>

            <div class="booking-summary-lines">
                @for (extra of summary().extras; track $index) {
                    <div class="d-flex justify-content-between gap-3">
                        <span>
                            {{ extra.quantity }} &times; {{ extra.name }}
                            @if (extra.modifierNames?.length) {
                                <small class="d-block text-secondary">{{ extra.modifierNames?.join(', ') }}</small>
                            }
                            @if (extra.unitAmount !== undefined) {
                                <small class="d-block text-secondary">{{ null | currencyUnit : extra.unitAmount : summary().currencyCode }} each</small>
                                <app-booking-product-refund-policy [policy]="extra" />
                            }
                        </span>
                        @if (extra.unitAmount !== undefined) {
                            <span>{{ null | currencyUnit : extra.unitAmount * extra.quantity : summary().currencyCode }}</span>
                        }
                    </div>
                }
            </div>
        </section>
    }

    @if (summary().payment; as payment) {
        <section
            class="booking-summary-segment booking-summary-section booking-summary-payment"
            aria-labelledby="booking-summary-payment-title"
            data-testid="booking-summary-payment"
        >
            <div>
                <h3
                    id="booking-summary-payment-title"
                    class="h6 fw-semibold mb-1"
                >
                    {{ payment.kind === 'deposit'
                        ? payment.label + ' due now'
                        : 'Card details required' }}
                </h3>
            </div>

            <div class="booking-summary-payment-amount">
                <span>
                    {{ null | currencyUnit : payment.amount : summary().currencyCode }}
                    @if (payment.isPerCover) {
                        per person
                    }
                </span>
                @if (payment.isPerCover) {
                    <span class="small text-secondary">
                        {{ null | currencyUnit : paymentTotal() : summary().currencyCode }} total
                    </span>
                }
            </div>
        </section>
    }
</aside>
